/**
 * MarketIntelTamPage — /omega/market-intel/tam
 *
 * TAM/SAM/SOM estimates viewer for the Market Intelligence Brain (Phase 40 V-025).
 * Fetches /api/v1/market-intel/tam?category={category}.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

const CATEGORIES = [
  'Financial',
  'HealthAndWellbeing',
  'TechnologyAndInfrastructure',
  'WorkAndProductivity',
];

interface TamEstimate {
  estimate_id: string;
  category: string;
  tam_usd: number;
  sam_usd: number;
  som_usd: number;
  // F-10: cents companions. Prefer these when present.
  tam_usd_cents?: number;
  sam_usd_cents?: number;
  som_usd_cents?: number;
  confidence: string;
  methodology: string;
  created_at: number;
}

interface TamResponse {
  estimates: TamEstimate[];
}

function fmtUsd(cents: number | undefined, usd: number) {
  // F-10: prefer the i64-cents field when available (avoids f64 rounding at boundary).
  const dollars = cents != null ? cents / 100 : usd;
  return '$' + (dollars / 1_000_000).toFixed(1) + 'M';
}

export function MarketIntelTamPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [category, setCategory] = useState('Financial');

  const { data, isLoading, error } = useQuery({
    queryKey: ['market-intel-tam', category],
    queryFn: () => apiFetch<TamResponse>(`/market-intel/tam?category=${category}`),
  });

  const estimates = data?.estimates ?? [];

  return (
    <div className="omega-page" style={{ padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Market Intel', to: '/omega/market-intel' },
          { label: 'TAM/SAM/SOM' },
        ]}
      />

      <h1 style={{ fontSize: '12pt', fontWeight: 700, margin: '12px 0 16px' }}>
        TAM / SAM / SOM Estimates
      </h1>

      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: '9pt', marginRight: 8 }}>Category:</label>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{ fontSize: '9pt', padding: '4px 8px' }}
        >
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {isLoading && <p style={{ fontSize: '9pt' }}>Loading...</p>}
      {error && <p style={{ fontSize: '9pt', color: '#f87171' }}>Error loading estimates.</p>}
      {!isLoading && estimates.length === 0 && (
        <p style={{ fontSize: '9pt', color: 'var(--omega-ink-muted)' }}>No estimates yet for {category}.</p>
      )}

      {estimates.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5pt' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ padding: '6px 10px' }}>TAM</th>
              <th style={{ padding: '6px 10px' }}>SAM</th>
              <th style={{ padding: '6px 10px' }}>SOM</th>
              <th style={{ padding: '6px 10px' }}>Confidence</th>
              <th style={{ padding: '6px 10px' }}>Method</th>
            </tr>
          </thead>
          <tbody>
            {estimates.map(e => (
              <tr key={e.estimate_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '6px 10px' }}>{fmtUsd(e.tam_usd_cents, e.tam_usd)}</td>
                <td style={{ padding: '6px 10px' }}>{fmtUsd(e.sam_usd_cents, e.sam_usd)}</td>
                <td style={{ padding: '6px 10px' }}>{fmtUsd(e.som_usd_cents, e.som_usd)}</td>
                <td style={{ padding: '6px 10px' }}>{e.confidence}</td>
                <td style={{ padding: '6px 10px' }}>{e.methodology}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
